import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Images, RefreshCcw, Copy, Check, Trash2, ExternalLink, ImageOff } from "lucide-react";

const TYPE_CLS = {
  product: "bg-emerald-50 text-emerald-800 border-emerald-200",
  ranch: "bg-amber-50 text-amber-800 border-amber-200",
  drop: "bg-rose-50 text-rose-800 border-rose-200",
  recipe: "bg-sky-50 text-sky-800 border-sky-200",
  ambassador: "bg-violet-50 text-violet-800 border-violet-200",
};

function prettyBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function AdminImageLibraryTab() {
  const [data, setData] = useState(null);
  const [folder, setFolder] = useState("all");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(`/admin/images${folder !== "all" ? `?folder=${folder}` : ""}`);
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load the image library.");
    } finally {
      setLoading(false);
    }
  }, [folder]);

  useEffect(() => { load(); }, [load]);

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(url);
    setTimeout(() => setCopied(""), 1600);
  };

  const remove = async (item) => {
    if (!window.confirm("Delete this photo from storage? This cannot be undone.")) return;
    setBusy(item.key);
    try {
      await api.delete("/admin/images", { data: { key: item.key } });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Delete failed");
    } finally {
      setBusy("");
    }
  };

  const items = data?.items || [];
  const folders = data?.folders || [];

  return (
    <div data-testid="admin-images-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <Images size={20} />
        <h2 className="font-display text-2xl">Image library</h2>
        <span className="text-xs text-[var(--ff-text-muted)] ml-3">
          {data ? `${data.total} photos · ${data.in_use} in use` : "…"}
        </span>
        <button
          onClick={load}
          className="ml-auto text-xs inline-flex items-center gap-1 text-[var(--ff-primary)] hover:underline"
          data-testid="images-refresh"
        >
          <RefreshCcw size={12} /> Refresh
        </button>
      </div>
      <p className="text-sm text-[var(--ff-text-muted)] mt-1">
        Every ranch, product, drop & ambassador photo uploaded to storage. Copy a URL to reuse it anywhere a photo field accepts a link.
      </p>

      <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="images-filters">
        <button
          onClick={() => setFolder("all")}
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${folder === "all" ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "bg-white border-stone-300 text-[var(--ff-text-secondary)] hover:border-[var(--ff-primary)]"}`}
          data-testid="images-filter-all"
        >
          All
        </button>
        {folders.map((f) => (
          <button
            key={f.id}
            onClick={() => setFolder(f.id)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${folder === f.id ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "bg-white border-stone-300 text-[var(--ff-text-secondary)] hover:border-[var(--ff-primary)]"}`}
            data-testid={`images-filter-${f.id}`}
          >
            {f.label} <span className="opacity-60">· {f.count}</span>
          </button>
        ))}
      </div>

      {err && (
        <div className="mt-6 card-flat p-6 text-center text-rose-700 flex items-center justify-center gap-2" data-testid="images-error">
          <ImageOff size={16} /> {err}
        </div>
      )}
      {loading && <p className="mt-6 text-[var(--ff-text-muted)]">Loading…</p>}
      {!loading && !err && items.length === 0 && (
        <div className="mt-8 card-flat p-10 text-center text-[var(--ff-text-muted)]" data-testid="images-empty">
          No uploaded photos in this folder yet.
        </div>
      )}

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="images-grid">
        {items.map((it) => (
          <div key={it.key} className="card-flat overflow-hidden group" data-testid={`image-card-${it.key}`}>
            <div className="relative aspect-[4/3] bg-stone-100">
              <img
                src={it.public_url}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.opacity = "0.15"; }}
              />
              <span className={`absolute top-2 left-2 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border bg-white/90 ${it.in_use ? "text-stone-600 border-stone-300" : "text-stone-400 border-stone-200"}`}>
                {it.folder_label}
              </span>
              {it.in_use && it.used_by && (
                <span className={`absolute top-2 right-2 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${TYPE_CLS[it.used_by.type] || "bg-stone-100 text-stone-600 border-stone-200"}`}>
                  In use
                </span>
              )}
            </div>
            <div className="p-3">
              <p className="text-xs text-[var(--ff-text-muted)] truncate" title={it.used_by?.label || it.key}>
                {it.used_by ? it.used_by.label : <span className="text-stone-400">Unused orphan</span>}
              </p>
              <p className="text-[11px] text-stone-400 mt-0.5">{prettyBytes(it.size)} · {(it.last_modified || "").slice(0, 10)}</p>
              <div className="mt-2 flex items-center gap-1.5">
                <button
                  onClick={() => copyUrl(it.public_url)}
                  className="flex-1 text-xs inline-flex items-center justify-center gap-1 border border-stone-300 hover:border-[var(--ff-primary)] rounded-full px-2 py-1.5 transition-colors"
                  data-testid={`image-copy-${it.key}`}
                >
                  {copied === it.public_url ? (<><Check size={12} /> Copied</>) : (<><Copy size={12} /> Copy URL</>)}
                </button>
                <a
                  href={it.public_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs inline-flex items-center justify-center border border-stone-300 hover:border-[var(--ff-primary)] rounded-full p-1.5 transition-colors"
                  title="Open original"
                  data-testid={`image-open-${it.key}`}
                >
                  <ExternalLink size={12} />
                </a>
                <button
                  onClick={() => remove(it)}
                  disabled={it.in_use || busy === it.key}
                  className="text-xs inline-flex items-center justify-center border rounded-full p-1.5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed text-rose-700 border-stone-300 hover:border-rose-400"
                  title={it.in_use ? "In use — remove it from its record first" : "Delete orphan"}
                  data-testid={`image-delete-${it.key}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
